<template>
  <div class="location-selector">
    <q-btn
      flat
      dense
      :round="iconOnlyToolbar"
      :label="iconOnlyToolbar ? undefined : currentLocationLabel"
      icon="location_on"
      class="location-btn"
    >
      <q-tooltip>{{ locationButtonTooltip }}</q-tooltip>

      <q-menu
        :offset="[0, 8]"
        class="location-menu"
      >
        <q-list style="min-width: 200px">
          <q-item-label header>
            <div class="text-weight-medium">Select Location</div>
          </q-item-label>

          <q-item
            v-for="location in locations"
            :key="location.id"
            clickable
            v-close-popup
            @click="setCurrentLocation(location)"
            :active="isCurrentLocation(location)"
            class="location-item"
          >
            <q-item-section avatar>
              <q-icon
                :name="isCurrentLocation(location) ? 'check_circle' : 'location_on'"
                :color="isCurrentLocation(location) ? 'positive' : 'grey'"
              />
            </q-item-section>

            <q-item-section>
              <q-item-label class="text-weight-medium">
                {{ location.name }}
              </q-item-label>
              <q-item-label caption>
                {{ location.city }}, {{ getStateLabel(location.state) }}
              </q-item-label>
            </q-item-section>
          </q-item>

          <q-separator />

          <q-item
            clickable
            v-close-popup
            @click="navigateToLocations"
            class="location-item"
          >
            <q-item-section avatar>
              <q-icon name="settings" color="primary" />
            </q-item-section>

            <q-item-section>
              <q-item-label class="text-weight-medium">
                {{ t('shared.manageLocations') }}
              </q-item-label>
              <q-item-label caption>
                {{ t('shared.manageLocationsCaption') }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-btn>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useLocationStore } from '@/stores/location'
import { useRegisterStore } from '@/stores/register'
import { useNotificationStore } from '@/stores/notification'
import { getStateLabel } from '@/utils/stateCodes'
import {
  LOCATION_SUCCESS_TOAST_OPTIONS,
  LOCATION_WARNING_TOAST_OPTIONS,
  resolveLocationName
} from '@/utils/registerLocationMessaging'

const { t } = useI18n()
const $q = useQuasar()
const router = useRouter()
const route = useRoute()

const iconOnlyToolbar = computed(() => $q.screen.lt.md)
const locationStore = useLocationStore()
const registerStore = useRegisterStore()
const notificationStore = useNotificationStore()

// Computed properties
const locations = computed(() => locationStore.getLocations)
const currentLocation = computed(() => locationStore.getCurrentLocation)

const currentLocationLabel = computed(() => {
  if (!currentLocation.value) {
    return t('shared.selectLocation')
  }

  // Truncate if too long
  const name = currentLocation.value.name
  return name.length > 20 ? name.substring(0, 20) + '...' : name
})

const locationButtonTooltip = computed(() => {
  if (iconOnlyToolbar.value) {
    return `${currentLocationLabel.value} - ${t('shared.switchLocation')}`
  }
  return t('shared.switchLocation')
})

// Methods
const isCurrentLocation = (location) => {
  return currentLocation.value && currentLocation.value.id === location.id
}

const setCurrentLocation = async (location) => {
  try {
    await locationStore.setCurrentLocation(location, {
      guard: async ({ targetLocation }) => {
        if (route.path !== '/sales/register') {
          return { allowed: true }
        }
        const active = await registerStore.loadCurrentUserRegister({ force: true, includeAllLocations: true })
        const activeLocationId = Number(active?.location_id)
        const targetLocationId = Number(targetLocation?.id)
        if (active?.is_open && activeLocationId && targetLocationId && activeLocationId !== targetLocationId) {
          const activeLocationName = active.location_name || resolveLocationName(activeLocationId, locationStore.getLocations)
          return {
            allowed: false,
            reason: t('shared.registerOpenInLocation', { location: activeLocationName, action: t('shared.closeBeforeSwitch') })
          }
        }
        return { allowed: true }
      }
    })
    notificationStore.success(t('shared.switchedTo', { name: location.name }), LOCATION_SUCCESS_TOAST_OPTIONS)
  } catch (error) {
    if (error?.blocked) {
      notificationStore.warning(error.message, LOCATION_WARNING_TOAST_OPTIONS)
    }
    // For backend failures, global axios handler will toast the backend message.
  }
}

const navigateToLocations = () => {
  router.push('/locations')
}

// Lifecycle
onMounted(async () => {
  // Locations are now initialized in MainLayout, so we don't need to fetch them here
  // The component will automatically update when the store is populated
})
</script>

<style lang="scss" scoped>
.location-selector {
  .location-btn {
    border-radius: 8px;
    padding: 8px 12px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.8);
    background: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .q-icon {
      margin-right: 4px;
    }
  }

  .location-menu {
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.2);

    .location-item {
      border-radius: 8px;
      margin: 2px 8px;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(128, 191, 239, 0.1);
      }

      &.q-item--active {
        background: rgba(128, 191, 239, 0.15);
        color: #000;
        font-weight: 500;
      }
    }
  }
}
</style>
